import { useCallback } from 'react';

import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

import { useUserMenuDataShapes } from '../../../../controllers/user-state/user-menu-data-shapes-provider';
import { useNavItemReorderingContext } from '../use-nav-item-reordering-context';

export function useReorderingActions(itemMenuId: string) {
	const { userMenuData, setUserMenuData } = useUserMenuDataShapes();
	const { index, moveUpPosition, moveDownPosition, menuTopPosition, menuBottomPosition } =
		useNavItemReorderingContext(itemMenuId);

	if (index === undefined) {
		throw new Error(`Item with ID ${itemMenuId} not found in user menu data`);
	}

	// Internal function to do the actual reorder actions
	const reorderItem = useCallback(
		async (startIndex: number, finishIndex: number) => {
			const reordered = reorder({
				list: userMenuData,
				startIndex,
				finishIndex,
			});

			setUserMenuData(reordered);
		},
		[setUserMenuData, userMenuData],
	);

	const moveUp = useCallback(() => {
		if (moveUpPosition === false || typeof moveUpPosition !== 'number') {
			return;
		}

		reorderItem(index, moveUpPosition);
	}, [index, moveUpPosition, reorderItem]);

	const moveDown = useCallback(() => {
		if (moveDownPosition === false || typeof moveDownPosition !== 'number') {
			return;
		}

		reorderItem(index, moveDownPosition);
	}, [index, moveDownPosition, reorderItem]);

	const moveToTop = useCallback(() => {
		if (
			moveUpPosition === false ||
			index === menuTopPosition ||
			typeof menuTopPosition !== 'number'
		) {
			return;
		}

		reorderItem(index, menuTopPosition);
	}, [moveUpPosition, index, menuTopPosition, reorderItem]);

	const moveToBottom = useCallback(() => {
		// If this is already the last reorderable item, do nothing
		if (
			moveDownPosition === false ||
			index === menuBottomPosition ||
			typeof menuBottomPosition !== 'number'
		) {
			return;
		}

		reorderItem(index, menuBottomPosition);
	}, [moveDownPosition, index, menuBottomPosition, reorderItem]);

	return {
		moveUp,
		moveDown,
		moveToTop,
		moveToBottom,
	};
}
